import { useContext, useMemo } from 'react';
import SortOption from '~/constants/SortOption';
import AppliedFiltersContext from '~/contexts/AppliedFiltersContext';
import { useHasAppliedTrade } from '~/contexts/SearchContext';
import { useVerboseLogging } from '~/contexts/StaticContext';
import { useUserLocation } from '~/contexts/UserContext';
import type { RequestedFeature } from '~/contracts';
import useGenerateAnalyticsData from '~/hooks/useGenerateAnalyticsData';
import type {
  InventoryRequest,
  InventoryRequestAnalytics
} from '~/services/contracts';
import { isDefined } from '~/utilities/array';
import BrowserCookies from '~/utilities/BrowserCookies';

const baseRequestedFeatures: RequestedFeature[] = [
  'EarliestAcquisitionBoosting',
  'LoanTermPricing',
  'LocationBasedPrefiltering',
  'Personalization',
  'TermsWithTradeInEquity' // TODO search-v2: remove once REO-750 is released
];

const dummyAnalyticsData: InventoryRequestAnalytics = {
  browser: '',
  clientId: '',
  deviceName: '',
  isFirstActiveSearchSession: false,
  isMobileDevice: false,
  previousSearchRequestId: '',
  referrer: '',
  searchRequestId: ''
};

type Options = {
  includeAnalyticsData: boolean;
  includePageNumber: boolean;
  limitRequestedFeatures: boolean;
};

const defaultOptions: Options = {
  includeAnalyticsData: true,
  includePageNumber: true,
  limitRequestedFeatures: false
};

export default function useGenerateInventoryRequest(
  pageSize: number,
  options: Partial<Options> = {}
): InventoryRequest {
  const opts: Options = { ...defaultOptions, ...options };

  const { appliedFilters, pageNumber, sortBy } = useContext(
    AppliedFiltersContext
  );
  const analyticsData = useGenerateAnalyticsData();
  const { zip = '' } = useUserLocation();
  const shouldApplySprinkles = sortBy === SortOption.MostPopular;
  const hasAppliedTrade = useHasAppliedTrade();
  const verboseLoggingEnabled = useVerboseLogging();

  const requestedFeatures = useMemo<RequestedFeature[]>(
    () =>
      opts.limitRequestedFeatures
        ? ['LocationBasedPrefiltering']
        : ([] as Array<RequestedFeature | undefined>)
            .concat(baseRequestedFeatures)
            .concat(hasAppliedTrade ? 'ApplyTradeIn' : 'FetchTradeIn')
            .concat(verboseLoggingEnabled ? 'ApplyVerboseLogging' : undefined)
            .concat(shouldApplySprinkles ? 'Sprinkles' : undefined)
            .filter(isDefined),
    [
      hasAppliedTrade,
      opts.limitRequestedFeatures,
      shouldApplySprinkles,
      verboseLoggingEnabled
    ]
  );
  const analyticsDataToSend = opts.includeAnalyticsData
    ? analyticsData
    : dummyAnalyticsData;
  const page = opts.includePageNumber ? pageNumber : 1;

  return useMemo<InventoryRequest>(
    () => ({
      analyticsData: analyticsDataToSend,
      browserCookieId: BrowserCookies.get('BrowserCookieId') ?? '',
      filters: appliedFilters,
      pagination: { page, pageSize },
      requestedFeatures,
      sortBy,
      zip5: zip
    }),
    [
      analyticsDataToSend,
      appliedFilters,
      page,
      pageSize,
      requestedFeatures,
      sortBy,
      zip
    ]
  );
}
